import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as docusignAPI from "../api/docusignAPI";
import { humanizeError } from "../utils/userFriendlyError";

const DS_SCRIPT_SRC = "https://www.docusign.com/scripts/ds-digital-signup.js";

function ensureScriptLoaded() {
  return new Promise((resolve, reject) => {
    if (window.DSDigitalSignup) {
      resolve();
      return;
    }
    let script = document.querySelector(`script[src="${DS_SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = DS_SCRIPT_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("Failed to load Docusign signup script.")));
  });
}

function DocusignTrialCallback() {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState("Connecting your Docusign account...");
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const docusignParam = searchParams.get("docusign");
    const errorCode = searchParams.get("error");

    const run = async () => {
      try {
        await ensureScriptLoaded();

        if (docusignParam === "connected") {
          setMessage("All set! Returning you to FastTrackr...");
          window.DSDigitalSignup?.notifySignupActivation?.(true);
          return;
        }

        if (docusignParam === "error") {
          setErrored(true);
          setMessage(
            errorCode
              ? `Docusign signup did not complete (${errorCode}). You can close this window and try again.`
              : "Docusign signup did not complete. You can close this window and try again."
          );
          try {
            window.opener?.postMessage(
              { type: "docusign-trial-error", error: errorCode || "unknown_error" },
              window.location.origin
            );
          } catch (_) {
            /* opener may be unavailable */
          }
          setTimeout(() => window.close(), 1500);
          return;
        }

        setMessage("Just a moment, redirecting to Docusign...");
        await docusignAPI.enable({ flow: "trial" });
      } catch (error) {
        setErrored(true);
        setMessage(humanizeError(error));
      }
    };

    run();
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20 p-6">
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl border border-emerald-100/50 shadow-xl p-10 max-w-md w-full text-center">
        {!errored && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-6"></div>
        )}
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {errored ? "Docusign signup" : "Connecting Docusign"}
        </h1>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );
}

export default DocusignTrialCallback;
